import * as React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useLocalStorage, useTokenRefresh } from '@/hooks';
import { useUiStore } from '@/store/uiStore';
import type { EchoKey } from '@/utils/constants';
import { SideBar } from '@/layouts/SideBar';
import { MobileSideBar } from '@/layouts/sidebar/MobileSideBar';

import clsx from 'clsx';
// Icons
import burgerWhite from '@/assets/images/svg/burger_icon.svg';
import burgerBlack from '@/assets/images/svg/burger_black_icon.svg';
import { NotificationButton } from '@/components/common/NotificationButton';
import { HeaderCredits } from '@/components/common/HeaderCredits';

/**
 * MainLayout
 *
 * App shell composing the desktop sidebar, mobile sidebar, mobile header,
 * and the routed `<main>` content column.
 *
 * Applies a width-based counter-zoom to `<main>` so high browser zoom /
 * Windows display scaling (up to 150%) renders at visual parity with
 * a 1920 CSS-px baseline. Mirrors the sidebar's own height-based zoom,
 * but uses width since the main column scrolls and has variable height.
 * Disabled below the xl breakpoint where the mobile layout takes over.
 */
export const MainLayout: React.FC = () => {
  const openModal = useUiStore((state) => state.openModal);
  const location = useLocation();
  const [selectedEcho, setSelectedEcho] = useLocalStorage<EchoKey>('selected_echo', '');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const mainRef = React.useRef<HTMLElement>(null);

  useTokenRefresh();

  React.useLayoutEffect(() => {
    const main = mainRef.current;
    if (!main) return;

    const updateScale = () => {
      const XL_BREAKPOINT = 1280;
      const TARGET_WIDTH = 1920;
      const MIN_SCALE = 1 / 1.5;
      if (window.innerWidth < XL_BREAKPOINT) {
        main.style.zoom = '1';
        return;
      }
      const scale = Math.max(MIN_SCALE, Math.min(1, window.innerWidth / TARGET_WIDTH));
      main.style.zoom = String(scale);
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  const handleLogout = () => {
    setIsMobileMenuOpen(false);
    openModal('logout');
  };

  const handleSupport = () => {
    setIsMobileMenuOpen(false); // Optional: close menu on mobile
    openModal('support');
  };

  React.useEffect(() => {
    setIsTransitioning(true);
    const timer = setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [isMobileMenuOpen]);

  const isHomePage = location.pathname.includes('/home');
  const burgerIcon = isHomePage ? burgerWhite : burgerBlack;
  const notificationTheme = isHomePage ? 'light' : 'dark';
  const isMessagesPage = location.pathname.includes('/messages');

  return (
    <div className='flex h-dvh w-full overflow-hidden bg-brand-25'>
      <SideBar onLogout={handleLogout} onSupport={handleSupport} />

      <MobileSideBar
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onLogout={handleLogout}
        onSupport={handleSupport}
      />

      {!isMessagesPage && (
        <div
          className={clsx(
            'absolute left-0 top-0 z-30 flex w-full items-center justify-between px-4 py-3 xl:hidden',
            isHomePage ? 'bg-brand-800' : 'bg-white'
          )}
        >
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className='pointer-events-auto -ml-2 p-2 transition-opacity hover:opacity-70 active:scale-95'
            aria-label='Open menu'
          >
            <img src={burgerIcon} alt='Menu' className='size-6 object-contain' />
          </button>

          <div className='pointer-events-auto -mr-2 flex items-center gap-2'>
            {isHomePage && (
              <div className='origin-right scale-[0.85]'>
                <HeaderCredits />
              </div>
            )}
            <NotificationButton theme={notificationTheme} />
          </div>
        </div>
      )}

      <main
        ref={mainRef}
        className={clsx(
          'relative z-0 flex min-w-0 flex-1 flex-col',
          !isMessagesPage && 'pt-[60px] xl:pt-0'
        )}
      >
        <Outlet context={{ selectedEcho, setSelectedEcho, setIsMobileMenuOpen, isTransitioning }} />
      </main>
    </div>
  );
};

MainLayout.displayName = 'MainLayout';
